// Dependencies
const dayjs = require('dayjs')
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
const utc = require('dayjs/plugin/utc')
const timezone = require('dayjs/plugin/timezone') // dependent on utc plugin
const customParseFormat = require('dayjs/plugin/customParseFormat')
const relativeTime = require('dayjs/plugin/relativeTime')

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

// Config
import config from '../config'

export const getSocketTransportOptions = (assistantId, tokens) => {
  // Retrieving token from 'botTokens' cookie based on assistantId
  try {
    tokens = tokens ? JSON.parse(tokens) : {}
  } catch (err) {
    tokens = {}
  }
  const token = assistantId in tokens ? 'Bearer ' + tokens[assistantId] : ''

  return {
    transportOptions: {
      polling: {
        extraHeaders: {
          Authorization: token
        }
      }
    },
    transports: ['polling', 'websocket']
  }
}

export const sanitizeHTML = html => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

/**
 * Check if a string is a valid url
 *
 * @param {String} url - url to validate
 * @returns {Boolean}
 */
export const isUrl = url => {
  const pattern = /(http|https):\/\/(\w+:{0,1}\w*)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/ // eslint-disable-line no-useless-escape
  return typeof url !== 'undefined' && url !== '' && pattern.test(url)
}

/**
 * gets all the url get parameters
 * @param {String} url current URL (document.location.href) or iframe parent url (document.referrer) if different from current location
 * @return {Object} vars
 */
const getUrlVars = url => {
  const vars = {}
  url.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (match, key, value) {
    vars[key] = value
  })

  return vars
}

/**
 * get Specific parameter from the url
 * @param {*} parameter
 * @param {*} defaultValue
 * @return {String} urlParameter
 */
export const getUrlParam = (parameter, defaultValue, url = window.location.href) => {
  let urlParameter = defaultValue

  if (url.indexOf(parameter) > -1) {
    urlParameter = getUrlVars(url)[parameter]
  }

  return urlParameter || defaultValue
}

/**
 * Load CSS dynamically
 * @param {String} fileName
 */
export const loadCssAsync = fileName => {
  const head = document.getElementsByTagName('head')[0]
  const yeldaCss = document.createElement('link')
  yeldaCss.rel = 'stylesheet'
  yeldaCss.type = 'text/css'
  yeldaCss.crossorigin = 'anonymous'
  yeldaCss.href = `${config.PLATFORM_CSS_BASE_URL}/${fileName}.css`
  yeldaCss.media = 'all'
  head.appendChild(yeldaCss)
}

/**
 * Return domain config
 * @param {String} host domain
 * @returns {Object} {PROTOCOL, HOST, NOTIFICATION_WAIT_TIME}
 */
export const getDomainConfig = host => {
  const env = getEnvName(host) // LOCALHOST or STAGING or PRODUCTION
  const envConfig = JSON.parse(JSON.stringify(config.ENV_CONST[env]))
  envConfig.IS_YELDA_WEB = false

  if (config.PRODUCTION !== env) {
    return envConfig
  }

  if (config.PROD_REGEX_HOST.WEB_DEMO.test(host)) {
    envConfig.IS_YELDA_WEB = true
    return envConfig
  }

  // Objective : a webchat loaded on xxxx.yelda.ai should ping xxxx.yelda.ai in a production context
  if (config.PROD_REGEX_HOST.YELDA.test(host)) {
    envConfig.HOST = host
  }

  return envConfig
}

/**
 * Get the environment from the domain
 * @param {String} href domain
 * @returns {String} env name LOCALHOST or STAGING or PRODUCTION
 */
const getEnvName = href => {
  const envs = Object.keys(config.DEV_REGEX_HOST)
  for (const env of envs) {
    if (config.DEV_REGEX_HOST[env].test(href)) {
      return env // LOCALHOST or STAGING
    }
  }

  return config.PRODUCTION
}

/**
 * Generate the websocket endpoint
 * @param {String} assistantId
 * @param {String} assistantSlug
 * @param {Object} domainConfig {PROTOCOL, HOST}
 * @returns {String} HTTP url
 */
export const getEndpoint = (assistantId, assistantSlug, domainConfig) => {
  return `${domainConfig.PROTOCOL}://${domainConfig.HOST}/api/website/${assistantId}/${assistantSlug}`
}

/**
 * Generate hash for the string
 * @param {String} slug
 * @returns {String} hashed string
 */
export const hashCode = slug => {
  let hash = 0
  let idx

  if (slug.length === 0) return hash
  for (idx = 0; idx < slug.length; idx++) {
    const chr = slug.charCodeAt(idx)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }

  return hash.toString(36)
}

/**
 * Checks user media available and also checks for available audio input
 * @returns {Promise<Object>}
 */
export const isUserMediaAvailable = () => {
  return new Promise(function (resolve, reject) {
    const isUserMediaAvailable =
      navigator &&
      ((navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia ||
        navigator.getUserMedia)

    // Extra check for IE11
    const mediaDevices = (window.navigator && window.navigator.mediaDevices) || navigator.mediaDevices

    if (!mediaDevices) {
      reject()
    }

    window.navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        devices = devices.filter(device => device.kind === 'audioinput' || device.kind === 'audio')

        if (devices.length && isUserMediaAvailable) {
          resolve()
        } else {
          reject()
        }
      })
      .catch(() => {
        reject()
      })
  })
}

/**
 * Get user media stream
 * @param {Object} constraints
 * @param {Boolean} constraints.audio
 * @returns {Promise<Object>}
 */
export const getUserMedia = constraints => {
  return new Promise(function (resolve, reject) {
    if (!navigator.mediaDevices) {
      navigator.mediaDevices = {}
    }

    let getUserMedia = null

    if (!navigator.mediaDevices.getUserMedia) {
      getUserMedia =
        navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia || navigator.getUserMedia
      if (!getUserMedia) {
        reject('getUserMedia is not supported in this browser')
      }

      //  wrap the call to the old navigator.getUserMedia with a Promise
      navigator.mediaDevices.getUserMedia = function (constraints) {
        return new Promise(function (resolve, reject) {
          getUserMedia.call(navigator, constraints, resolve, reject)
        })
      }
    }

    let hasGetUserMediaStreamStarted = false
    const GET_USER_MEDIA_WAITING_TIME = 5000

    // In some browsers like Firefox, especially mobile version does not trigger the callback if the prompt is
    // ignored, so we reject after GET_USER_MEDIA_WAITING_TIME ms
    const timeout = setTimeout(() => {
      if (!hasGetUserMediaStreamStarted) {
        reject()
      }
    }, GET_USER_MEDIA_WAITING_TIME)

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        resolve(stream)
      })
      .catch(error => {
        reject(error)
      })
      .finally(() => {
        if (timeout) {
          clearTimeout(timeout)
        }
        hasGetUserMediaStreamStarted = true
      })
  })
}

export const mixin = {
  methods: {
    /**
     * formatMessage method do the following
     * 1. sanitize(strip) the HTML content if present in the string
     * 2. Convert '\n' to '<br />' tags for paragraph breaks
     * 3. Convert markdown to HTML (links, bold, italic and lists)
     * @param {string} text
     * @returns {string}
     */
    formatMessage(text) {
      // Remove HTML tags
      text = sanitizeHTML(text, { allowedTags: [] })

      // Just in case, change ↵ character to new line
      text = text.replace(/↵/g, '\n')

      // Markdown regex taken from https://gist.github.com/renehamburger/12f14a9bd9297394e5bd

      //       Regex Fragment      | Meaning
      // ===========================================
      //  \[([^\[]+)\]\(([^\)]+)\) | Handle markdown links ([description](link))
      //  \[        \]             | Match characters between []
      //    (      )               | First capturing group
      //     [^\[]+                | Match one to unlimited characters but "["
      //              \(        \) | Match characters between ()
      //                (      )   | Second capturing group
      //                 [^\)]+    | Match one to unlimited characters but ")"
      text = text.replace(/\[([^\[]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank">$1</a>')

      // Handle solo links, will match every string beginning with http[s]://, finishing with a whitespace
      text = text.replace(/(https?:\/\/[^\s]+)/g, url => {
        // We don't want to replace already parsed links (<a href="")
        // so if there is a double quote, it's probably a link in an HTML <a> tag
        if (url.includes('"')) {
          return url
        } else {
          return `<a href="${url}" target="_blank">${url}</a>`
        }
      })

      // Handle markdown bold (double ** and __)
      text = text.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>')

      //       Regex Fragment        | Meaning
      // ===========================================

      //  (^_|\s_)((?!_).*?)(?:\_) | Handle markdown italic (single _)
      // Handle markdown lists, it creates an ul/ol tag for every item, extra tags will get removed after
      //  (      )                 | First group, capturing
      //   ^_|\s_                  | Start of string or whitespace followed by _
      //          (        )       | Second group, capturing
      //           (?!_)           | Should not be followed by _
      //                .*?        | Any following characters except for line terminators
      //                    (?:  ) | Third group, non capturing
      //                       \_  | Finish with the character _
      text = text.replace(/(^|\s)(?:\_)((?!_).*?)(?:\_)/g, '$1<em>$2</em>')

      // Handle markdown italic (single *)
      text = text.replace(/(\*)(.*?)\1/g, '<em>$2</em>')

      // Handle markdown lists, it creates an ul tag for every item, extra tags will get removed after
      // Unordered lists created from lines beginning with -
      text = text.replace(/(?:^|\n)-(.*)/g, (text, item) => {
        return `\n<ul><li>${item.trim()}</li></ul>`
      })

      // Remove extra ul tags created above
      text = text.replace(/<\/ul>\s?<ul>/g, '')

      // Replace new lines with br tags
      text = text.replace(/\n/g, '<br>')

      return text
    },
    isUrl
  }
}

/**
 * Return true if the last message time is far enough (acceptableWaitingTime) from currentTime
 * @param {Number} messageReceivedTime from cookie `messageReceivedTime${this.assistantId}`
 * @param {Number} acceptableWaitTime number in seconds
 *
 * @return {Boolean}
 */
export const isEventAcceptableInTime = (messageReceivedTime, acceptableWaitTime) => {
  const currentTime = Math.floor(Date.now() / 1000)

  // Check for last message received time
  if (messageReceivedTime && currentTime - messageReceivedTime < acceptableWaitTime) {
    return false
  }

  return true
}

/**
 * https://stackoverflow.com/questions/44307140/localizing-day-and-month-in-moment-js
 *
 * Get day and month from date
 * @param {Date} date
 * @return {String} formatted date without year
 */
export function getDayAndMonth(date) {
  const formattedDate = dayjs(date).format('LL')
  return formattedDate
    .replace(dayjs(date).format('YYYY'), '') // remove year
    .replace(/\s\s+/g, ' ') // remove double spaces, if any
    .trim() // remove spaces from the start and the end
    .replace(/[рг]\./, '') // remove year letter from RU/UK locales
    .replace(/de$/, '') // remove year prefix from PT
    .replace(/b\.$/, '') // remove year prefix from SE
    .trim() // remove spaces from the start and the end
    .replace(/,$/g, '') // remove comma from the end
}

/**
 * Ensure that the function is a valid fonction, return false and log in case of error
 * @param {Function} expectedFunction
 * @param {Object} vNode
 * @return {Boolean}
 */
export function isFunctionType(expectedFunction, vNode) {
  if (typeof expectedFunction !== 'function') {
    const compName = vNode.context.name
    let warn = `[longclick:] provided expression '${expectedFunction.name}' is not a function, but has to be`
    if (compName) {
      warn += `Found in component '${compName}' `
    }
    console.warn(warn)
    return false
  }
  return true
}

export function formatDate(sentTime, locale, i18n, isIncludeTime = true) {
  const formattedLocale = locale && locale.split('_')[0]

  // If no locale, dayjs fallback to 'en' automatically
  if (formattedLocale) {
    dayjs.locale(formattedLocale)
  }

  // Add plugin for localized format
  dayjs.extend(LocalizedFormat)

  const formattedTime = dayjs(sentTime).format('LT') // "14:04"

  // If message date same as today, only return time
  if (dayjs().isSame(dayjs(sentTime), 'day')) {
    return formattedTime
  }

  const dateLastYear = dayjs().subtract(1, 'years')
  const formattedDate = dayjs(sentTime).isBefore(dateLastYear)
    ? dayjs(sentTime).format('LL') // "February 14th 2019"
    : getDayAndMonth(sentTime) // "February 14th"

  return isIncludeTime ? i18n('message_sent_time_label', { date: formattedDate, time: formattedTime }) : formattedDate
}

/**
 * Checks whether we can show the intent, action logs
 *
 * @param {Object} context
 * @param {Object} intentAnalysed
 * @param {Object} actionDeduced
 * @param {Boolean} isShowLogs
 * @returns {Boolean}
 */
export function areDetailsShown(context, intentAnalysed, actionDeduced, isShowLogs) {
  return (
    (isShowLogs &&
      ((intentAnalysed && intentAnalysed.hasOwnProperty('intentName')) ||
        (context &&
          (context.hasOwnProperty('isLastActionInProgress') ||
            context.hasOwnProperty('nbChainedNotUnderstoodIntents'))) ||
        (actionDeduced && actionDeduced.hasOwnProperty('action')))) ||
    false
  )
}

/**
 * Scrolls to bottom of the chat list
 * @param {Boolean} isNewMessage
 */
export function scrollToBottom(isNewMessage) {
  if (isNewMessage) {
    document.getElementById('chatList').scrollTop = document.getElementById('chatList').scrollHeight
  }
}

/**
 * Correct user voice message for misspelled word based on nlp word rules
 * @param {String} message - user message
 * @param {Array<Object>} nlpWordRules - {wordToReplace <String>, replacementWord <String>}
 * @returns {String} correct user message if any misspelled word presents in the message
 */
export function correctMessageBasedOnWordRules(message, nlpWordRules) {
  for (const rule of nlpWordRules) {
    if (!message.includes(rule.wordToReplace)) {
      continue
    }

    // Replace all occurrence of the word
    const regex = new RegExp(`${rule.wordToReplace}`, 'g')
    message = message.replace(regex, rule.replacementWord)
  }

  return message
}

/**
 * Scroll the chat window to the new message
 */
export function scrollToNewMessage() {
  setTimeout(function () {
    const chatList = document.getElementById('chatList')
    chatList.scrollTop = chatList.scrollHeight
  }, 500)
}

/**
 * Checks the message origin is from server
 * @param {String} origin
 * @returns {Boolean}
 */
export function isServerMessage(origin) {
  return origin === config.ORIGIN_TYPES.SERVER
}

/**
 * Checks the message origin is from client
 * @param {String} origin
 * @returns {Boolean}
 */
export function isClientMessage(origin) {
  return origin === config.ORIGIN_TYPES.CLIENT
}

/**
 * Return if the new message is the start of a new conversation
 * @param {Object} lastMessage previous message { conversationNumber, origin, ...}
 * @param {String} expectedMessageOrigin current message origin ORIGIN_TYPES.CLIENT ('client') or ORIGIN_TYPES.SERVER ('server')
 * @param {Object} socketEventData received data from socket event
 * @returns {Boolean}
 */
export function isConversationStart(lastMessage, expectedMessageOrigin, socketEventData) {
  if (!socketEventData.isNewConversation) {
    return false
  }

  if (!lastMessage) {
    return true
  }

  // If origin not present then assign SERVER origin to the message
  if (!lastMessage.origin) {
    lastMessage.origin = config.ORIGIN_TYPES.SERVER
  }

  const isConversationNumberEqual = lastMessage.conversationNumber === socketEventData.conversationNumber

  // if it is obviously (different conversationNumber) a new conversation, return true
  if (lastMessage.conversationNumber && !isConversationNumberEqual) {
    return true
  }

  // when the last client message is from different conversation
  if (lastMessage.conversationNumber && isClientMessage(expectedMessageOrigin)) {
    return false
  }

  // When the last server message but the expected message origin is client then return false
  if (isServerMessage(lastMessage.origin) && isClientMessage(expectedMessageOrigin)) {
    return false
  }

  // When last client message does not have conversation number it will considered as start of conversation
  if (
    isClientMessage(lastMessage.origin) &&
    isServerMessage(expectedMessageOrigin) &&
    (!lastMessage.conversationNumber || isConversationNumberEqual)
  ) {
    return false
  }

  return true
}

/**
 * Get formatted today's date in YYYY/MM/DD format
 * @param {String} timeZone
 * @returns {String}
 */
export function getTodayDate(timeZone) {
  return dayjs().tz(timeZone).format(config.NOTIFICATION_DATE_FORMAT)
}

/**
 * Get date day in specific time zone
 * @param {String} dateDay
 * @param {String} timeZone
 * @returns {String} dateDay in YYYY/MM/DD format
 */
export function getDateDayInSpecificTimeZone(dateDay, timeZone) {
  return dayjs(dateDay, config.NOTIFICATION_DATE_FORMAT).tz(timeZone).format(config.NOTIFICATION_DATE_FORMAT)
}

/**
 * Get the image S3 path for the emoji
 * @param {String} imageName
 * @returns {String}
 */
export function getEmojiImagePath(imageName) {
  return config.EMOJI_S3_URL.replace('{imageName}', imageName)
}

/**
 * Check the provided string is JSON or not
 * @param {String} string
 * @returns {Boolean}
 */
export function isJSON(string) {
  try {
    JSON.parse(string)
    return true
  } catch (err) {
    return false
  }
}

/**
 * Check the provided variable is array and each object contains single key value pair
 * @param {Object[]} arr
 * @returns {Boolean}
 */
export function isArrayWithSingleKeyValuePairs(arr) {
  // Check if arr is an array
  if (!Array.isArray(arr) || !arr.length) {
    return false
  }

  // Check each object in the array
  for (const obj of arr) {
    // Check if the object has exactly one key-value pair
    const keys = Object.keys(obj)
    if (keys.length !== 1 || typeof obj[keys[0]] === 'undefined') {
      return false
    }
  }

  // If all objects in the array passed the check
  return true
}

/**
 * Extract the webchat slots from the yparam
 * @param {String|null} yparamURLParam
 * @returns {Object[]|null}
 */
export function getWebchatSlots(yparamURLParam) {
  if (!yparamURLParam) {
    return null
  }

  const yparam = decodeURIComponent(yparamURLParam)

  if (!yparam || typeof yparam !== 'string' || !isJSON(yparam)) {
    return null
  }

  const parsedYparam = JSON.parse(yparam)
  if (!parsedYparam || !('slots' in parsedYparam) || !isArrayWithSingleKeyValuePairs(parsedYparam.slots)) {
    return null
  }

  return parsedYparam.slots
}

/**
 * Get the relative time of a date from now.
 * @param {string} date - The input date (2025-08-27T12:34:56.679Z).
 * @param {string} locale - The locale to set for dayjs ('en_US').
 * @returns {string} - The relative time from now ('2 days ago').
 */
export const getRelativeTime = (date, locale) => {
  const formattedLocale = locale && locale.split('_')[0]
  // If no locale, dayjs fallback to 'en' automatically
  if (formattedLocale) {
    dayjs.locale(formattedLocale)
  }
  return dayjs(date).fromNow()
}

export function getMessagesFromConversations(conversations) {
  const messages = []
  let lastConversationId = null
  let isStartOfConversationAssigned = false

  conversations.forEach(conversation => {
    let copiedConversation = null
    try {
      copiedConversation = JSON.parse(JSON.stringify(conversation))
    } catch (error) {
      // Skip if conversation is not valid JSON
      return
    }
    // Exclude the event type message
    const eventMessage = copiedConversation.message.find(message => message.event)
    if (eventMessage) {
      return
    }
    /*
     * if the conversation.conversationId coming from backend is different from current lastConversationId,
     * it means we are starting a new conversation, and thus we set isStartOfConversation to true
     * So we can display a separation between the previous and the new conversation in the UI
     */
    let isStartOfConversation = false
    if (!isStartOfConversationAssigned || lastConversationId !== copiedConversation.conversationId) {
      lastConversationId = copiedConversation.conversationId
      isStartOfConversation = true
      isStartOfConversationAssigned = true
    }

    copiedConversation.message.forEach(message => {
      message.sentTime = copiedConversation.sentTime
      message.origin = config.ORIGIN_TYPES[copiedConversation.senderType]

      if (copiedConversation.adminId) {
        message.adminId = copiedConversation.adminId
      }
    })

    const { ...conversationDetails } = copiedConversation
    messages.push({
      isStartOfConversation: isStartOfConversation,
      ...conversationDetails
    })
  })

  return messages
}

/**
 * converts the messages object from server to normalized format for the rendering purpose
 * @param {Object} messages
 * @param {Boolean} isOldConversation default false, passed true when called while getting the old conversation
 * @returns {Object} formattedMessages
 */
export function getNormalizedMessages(messages, isOldConversation = false) {
  const formattedMessages = []
  messages.forEach(messageData => {
    let copiedMessageData = null
    try {
      copiedMessageData = JSON.parse(JSON.stringify(messageData))
    } catch (error) {
      // Skip if messageData is not valid JSON
      return
    }
    // eslint-disable-next-line no-unused-vars
    const { isStartOfConversation, senderType, ...messageDetails } = copiedMessageData
    copiedMessageData.message.forEach((element, index) => {
      // Do not show the tracking interaction in the conversation
      if (element.event && Object.values(config.EVENT_TYPES).includes(element.event.eventType)) {
        return
      }

      element.origin = element.origin || config.ORIGIN_TYPES.SERVER
      /*
        Copy `messageDetails` to avoid assigning a reference to it in `element`
        Otherwise we end up with a circular structure
        meaning that would `element` contains itself since it is in `messageDetails.message`
        which is what we are currently updating

        INITIAL FORMAT:
        {
          "_id": "",
          "messageType": "message",
          "payload": {},
          "mainContent": "my answer",
          "sentTime": 1641221561091,
          "origin": "server"
        }

        FINAL FORMAT:
        {
          "_id": "",
          "messageType": "message",
          "payload": {},
          "mainContent": "my answer",
          "sentTime": 1641221561091,
          "origin": "server",
          "conversationId": "",
          "intentAnalysed": null,
          "actionDeduced": {},
          "context": {
            "isLastActionInProgress": false,
            "previousAction": null,
            "nbWelcomes": 0,
            "nbChainedNotUnderstoodIntents": 0,
            "nbChainedFallback": 0,
            "forwardedAssistantId": null,
            "currentActionSlots": [],
            "previousActionsToResume": [],
            "persistantSlots": []
          },
          "isNewMessage": true,
          "conversationNumber": 1
        }
      */
      Object.assign(element, JSON.parse(JSON.stringify(messageDetails)))
      // Also delete `message` since the element does not need this property
      delete element.message
      // assign isStartOfConversation for the first message in conversation
      element.isStartOfConversation = isStartOfConversation && index === 0

      if (config.SIMPLE_MESSAGES.indexOf(element.messageType) > -1) {
        // Simple Message Format and chips
        formattedMessages.push(element)
      } else if (element.chips) {
        // Chips that are already grouped don't need to use groupMessagesElementByType anymore (`element.chips` already exists)
        // Chips also contains the group labels (messageType === 'chipsTitle') but for now we don't want to display them,
        // so let's only keep the chip buttons
        element.chips = element.chips.filter(chip => chip.messageType === config.MESSAGE_TYPES.CHIP)
        formattedMessages.push(element)
      } else {
        // Group Message according to the messageType
        groupMessagesElementByType(formattedMessages, element, config.TYPES_GROUP[element.messageType])
      }
    })

    // Group Form type elements into together if found
    const formChip = formattedMessages.slice(0).find(obj => {
      return config.MESSAGE_TYPES.FORM_CHIPS in obj
    })
    const formItem = formattedMessages.slice(0).find(obj => {
      return config.MESSAGE_TYPES.FORM_ITEMS in obj
    })

    if (formItem) {
      formItem[config.MESSAGE_TYPES.FORM_TYPE] = {
        [config.MESSAGE_TYPES.FORM_ITEMS]: formItem[config.MESSAGE_TYPES.FORM_ITEMS],
        ...(formChip && {
          [config.MESSAGE_TYPES.FORM_CHIPS]: formChip[config.MESSAGE_TYPES.FORM_CHIPS]
        }),
        isFieldsDisabled: isOldConversation
      }
    }

    // Add isClickDisabled to the carousel items if old conversation loaded
    const carouselItems = formattedMessages.slice(0).find(obj => {
      return config.MESSAGE_TYPES.CAROUSEL_ITEMS in obj
    })

    if (carouselItems) {
      carouselItems.isClickDisabled = isOldConversation
    }
  })

  return formattedMessages
}

/**
 * groups the message elements by the type (chips, carousel items, form elements)
 * @param {Array<Object>} formattedMessages list of already processed message
 * @param {Object} element currently checked message
 * @param {String} type detected Message grouping type
 * E.g. grouped chips
 * BEFORE
 * [{ _id: '', origin: 'server', messageType: 'chip', text: 'QR 1' }, { _id: '', origin: 'server', messageType: 'chip', text: 'QR 2' }]
 *
 * AFTER
 * [{
 *   _id: '',
 *  origin: 'server',
 *  messageType: 'chip',
 *  chips: [{ _id: '', origin: 'server', messageType: 'chip', text: 'QR 1' }, { _id: '', origin: 'server', messageType: 'chip', text: 'QR 2' }]
 * }]
 */
function groupMessagesElementByType(formattedMessages, element, type) {
  // e.g. chipsTitle messageType don't have a matching TYPES_GROUP because we don't want to show it in the webchat
  // So let's skip the elements we don't want to show
  if (!type) {
    return
  }

  const obj = formattedMessages.slice(0).find(function (obj) {
    return obj.messageType === element.messageType
  })

  if (obj && Array.isArray(obj[type])) {
    obj[type].push(element)
  } else {
    formattedMessages.push({
      _id: element._id,
      messageType: element.messageType,
      origin: element.origin,
      [type]: [element]
    })
  }
}
